import { useCallback, useMemo, useRef, useState } from "react";
import {
	CalendarEvent,
	CLIENT_PROJECT_SEPARATOR,
	Project,
	ProjectsSortedByTime,
	UNNAMED_PROJECT,
} from "./App";
import { IconCaretDown, IconCheck, IconDone } from "./Icons";
import { refetchState } from "./state";

interface ProjectsListProps {
	projectsSortedByTime: ProjectsSortedByTime;
	disableFilter?: boolean;
	ignoreDone?: boolean;
}

async function updateEvent(
	accessToken: string,
	calendarId: string,
	eventId: string,
	updates: Record<string, any>,
) {
	const res = await fetch(
		`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
			calendarId,
		)}/events/${encodeURIComponent(eventId)}`,
		{
			method: "PATCH",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(updates),
		},
	);

	if (!res.ok) {
		throw new Error(`Failed to update event: ${res.status}`);
	}

	return res.json();
}

export function ProjectsList(props: ProjectsListProps) {
	const [searchWords, setSearchWords] = useState<string[]>([]);
	const [onlyShowNonCompleted, setOnlyShowNonCompleted] = useState(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const updateSearchWords = useCallback(
		(term: string) => {
			if (!term) {
				setSearchWords([]);
			} else {
				setSearchWords(term.toUpperCase().split(" "));
			}
		},
		[setSearchWords],
	);
	return (
		<>
			{props.projectsSortedByTime.keys.length > 0 && (
				<>
					{!props.disableFilter && (
						<div className="flex flex-col items-start pb-3 text-sm">
							<input
								ref={searchInputRef}
								type="search"
								className=" border rounded mb-2 text-sm px-2 h-7 "
								placeholder="Search"
								onChange={(e) => {
									updateSearchWords(e.target.value);
								}}
								autoFocus
							/>
							<label>
								<input
									type="checkbox"
									checked={onlyShowNonCompleted}
									onChange={() =>
										setOnlyShowNonCompleted((x) => !x)
									}
								/>{" "}
								Only show non completed projects
							</label>
						</div>
					)}

					<table className="text-sm w-5xl">
						<thead>
							<tr className=" font-bold ">
								<td className="pr-10">Customer</td>
								<td className="pr-10">Project</td>
								<td className="pr-10 text-right">Hours</td>
								<td className="pr-5">Time period</td>
								<td></td>
							</tr>
						</thead>
						<tbody>
							{props.projectsSortedByTime.keys.map((x) => {
								const p =
									props.projectsSortedByTime.projects[x];
								if (!p) throw new Error("No project for " + x);
								return (
									<SingleListItem
										ignoreDone={props.ignoreDone}
										disableFilter={props.disableFilter}
										key={x}
										project={p}
										projectKey={x}
										onlyShowNonCompleted={
											onlyShowNonCompleted
										}
										searchWords={searchWords}
										setSearchWords={(value) => {
											if (searchInputRef.current) {
												searchInputRef.current.value =
													value;
												updateSearchWords(value);
												searchInputRef.current.focus();
											}
										}}
									/>
								);
							})}
						</tbody>
					</table>
				</>
			)}
		</>
	);
}

interface SingleListItemProps {
	projectKey: string;
	project: Project;
	onlyShowNonCompleted: boolean;
	searchWords: string[];
	setSearchWords: (w: string) => void;
	ignoreDone?: boolean;
	disableFilter?: boolean;
}

function SingleListItem(props: SingleListItemProps) {
	// state
	const [isOpen, setIsOpen] = useState(false);
	const setNeedsFetch = refetchState((x) => x.setNeedsFetch);
	const incrIsFetching = refetchState((x) => x.incrIsFetching);
	const decrIsFetching = refetchState((x) => x.decrIsFetching);
	const isFetching = refetchState((x) => x.isFetching);

	const p = props.project;

	const visible =
		props.searchWords.length < 1 ||
		props.searchWords.every((w) => {
			if (!w) return true;
			return props.projectKey.toUpperCase().includes(w);
		});

	if (!p) throw new Error("No project for " + props.projectKey);
	let [customer, project] = props.projectKey.split(CLIENT_PROJECT_SEPARATOR);
	project = project ?? UNNAMED_PROJECT;
	const fromToString = useMemo(() => {
		if (p.events.length <= 0) return "?";
		const firstEventDate = p.events[0].start?.dateTime;
		const lastEventDate = p.events[p.events.length - 1].start?.dateTime;
		if (firstEventDate && lastEventDate) {
			const startDate = new Date(firstEventDate);
			const lastDate = new Date(lastEventDate);
			const startDateString = new Intl.DateTimeFormat("sv-SE", {
				timeZone: "Europe/Stockholm",
				day: "2-digit",
				month: "short",
				year: "numeric",
			}).format(startDate);
			const endDateString = new Intl.DateTimeFormat("sv-SE", {
				timeZone: "Europe/Stockholm",
				day: "2-digit",
				month: "short",
				year: "numeric",
			}).format(lastDate);
			if (startDateString === endDateString) {
				return startDateString;
			}
			return `${startDateString} – ${endDateString}`;
		}
	}, [p.events]);

	const isDone = !props.ignoreDone ? p.done : false;

	// Was the project marked as DONE, but then were new events added afterwards??
	const addedEventsAfterDone = useMemo(() => {
		if (!isDone) return false;
		const lastEvent = p.events[p.events.length - 1];
		if (lastEvent.summary?.includes("DONE")) return false;
		return true;
	}, [p.events, isDone]);

	const hideDone =
		props.onlyShowNonCompleted && p?.done && !addedEventsAfterDone;

	return !visible || hideDone ? null : (
		<>
			<tr
				className={`
						${isFetching > 0 && !isDone ? "opacity-50" : ""}
            ${isDone && !addedEventsAfterDone ? " line-through opacity-30 " : ""}
						${addedEventsAfterDone ? "bg-red-100" : ""}
            h-7 border-b border-b-gray-100
            `}
			>
				<td className="pr-10 w-md">
					<span
						className={`${!props.disableFilter && " cursor-pointer underline "}`}
						onClick={() => {
							props.setSearchWords(customer.replace("#", ""));
						}}
					>
						{customer}
					</span>
					{addedEventsAfterDone && (
						<strong> Events added after marked as done!</strong>
					)}
				</td>
				<td className="pr-10 w-[180px]">{project}</td>
				<td className="pr-10 text-right tabular-nums ">
					{(p.time / 1000 / 60 / 60).toFixed(2)} h
				</td>
				<td className="pr-5 tabular-nums ">{fromToString}</td>
				<td className="flex gap-2 justify-end">
					{!props.ignoreDone && (
						<button
							title={
								isDone
									? "Mark project as NOT done"
									: "Mark project as done"
							}
							disabled={isFetching > 0}
							className={` ${isFetching ? " cursor-not-allowed " : " cursor-pointer "} `}
							onClick={async () => {
								const token = localStorage.getItem(
									"google_access_token",
								);
								const calendarId =
									localStorage.getItem("active_calendar_id");
								if (token && calendarId) {
									let events: CalendarEvent[] = [];
									if (isDone) {
										events = p.events.filter((x) =>
											x.summary?.includes("DONE"),
										);
									} else {
										events = [
											p.events[p.events.length - 1],
										];
									}
									try {
										incrIsFetching();
										await Promise.all(
											events.map((e) => {
												let newSummary =
													e.summary?.replaceAll(
														"DONE",
														"",
													);
												if (!isDone) {
													newSummary += " DONE";
												}
												return updateEvent(
													token,
													calendarId,
													e.id,
													{
														summary: newSummary,
													},
												);
											}),
										);
										setNeedsFetch(Date.now());
									} catch (e) {
										console.log(e);
									} finally {
										decrIsFetching();
									}
								}
							}}
						>
							{isDone ? <IconCheck /> : <IconDone />}
						</button>
					)}
					<button
						title={"Show all events in project"}
						className={`${isOpen ? " rotate-180 " : "rotate-0"} transition-transform p-1 cursor-pointer `}
						onClick={() => setIsOpen((x) => !x)}
					>
						<IconCaretDown />
					</button>
				</td>
			</tr>
			{isOpen && (
				<>
					<tr className=" text-xs bg-gray-50 h-8 ">
						<td className="pr-10 pl-3 w-md font-black">Summary</td>
						<td className="pr-5 w-[180px] font-bold">Time</td>
						<td className="text-right pr-10 font-bold">Duration</td>
						<td></td>
						<td></td>
					</tr>
					{p.events.map((e) => {
						const startDate = new Date(e.start?.dateTime || "");
						const endDate = new Date(e.end?.dateTime || "");
						const startDateFormatted = new Intl.DateTimeFormat(
							"sv-SE",
							{
								dateStyle: "short",
								timeStyle: "short",
								timeZone: "Europe/Stockholm",
							},
						).format(startDate);
						const endDateFormatted = new Intl.DateTimeFormat(
							"sv-SE",
							{
								timeStyle: "short",
								timeZone: "Europe/Stockholm",
							},
						).format(endDate);
						const duration =
							endDate.getTime() - startDate.getTime();
						return (
							<tr className=" text-xs h-5 bg-gray-50 " key={e.id}>
								<td className="pr-5 pl-3 w-md">{e.summary}</td>
								<td className="pr-5 w-[180px] tabular-nums">
									{startDateFormatted}–{endDateFormatted}
								</td>
								<td className="text-right pr-10">
									{(duration / 1000 / 60 / 60).toFixed(2)} h
								</td>
								<td></td>
								<td></td>
							</tr>
						);
					})}
					{/*Extra space after table*/}
					<tr className="bg-gray-50 h-4 ">
						<td colSpan={5} />
					</tr>
				</>
			)}
		</>
	);
}
